import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import PublicPageHero from '@/components/PublicPageHero';
import MenuImageBreak from '@/components/MenuImageBreak';
import { MENU_HERO, HERO_OBJECT_POSITION } from '@/data/marketing/photos';

const MENU_BREAKS: Record<number, { photo: string; quote: string; attribution?: string }> = {
  // Après la section 01 (bouffe)
  0: {
    photo: '/images/marketing/0R9A2034.webp',
    quote: 'Le tartare de la Boucherie du Brack, fraîchement coupé chaque jour.',
    attribution: 'Notre signature',
  },
  // Après la section 02 (drinks)
  1: {
    photo: '/images/marketing/0R9A2017.webp',
    quote: 'Une nouvelle bouteille rejoint la carte chaque mois.',
    attribution: "L'esprit Alambic",
  },
};
import { fadeUp, scaleIn, staggerContainer } from '@/lib/animations';

/**
 * Page Menu publique — vitrine de l'offre du Bar L'Alambic.
 * 6 sections avec subnav sticky.
 */

interface Section {
  id: string;
  num: string;
  title: string;
  subtitle: string;
  groups: Array<{
    label?: string;
    items: Array<{ name: string; desc?: string; price?: string }>;
  }>;
}

const SECTIONS: Section[] = [
  // ═══════════════════════════════════════════════════════════
  // 01 — BOUFFE
  // ═══════════════════════════════════════════════════════════
  {
    id: 'bouffe',
    num: '01',
    title: 'Bouffe',
    subtitle: 'Tartares, fromages d\'ici, plateaux à partager',
    groups: [
      {
        label: 'Entrées',
        items: [
          { name: 'Salade Grecque', price: '8,99 $' },
          { name: 'Chips Tostitos avec sauce salsa', price: '6,99 $' },
          { name: 'Quepasa', price: '11,99 $' },
        ],
      },
      {
        label: 'Les fromages d\'ici',
        items: [
          {
            name: 'Plateau de fromage et charcuterie de l\'Alambic (4 personnes)',
            desc: 'Fromage Rose et Fleur d\'ail complet de la Vache à Maillotte, saucissons, cornichons, olives et oitas',
            price: '30,99 $',
          },
          {
            name: 'Plateau de fromage et charcuterie de l\'Alambic (2 personnes)',
            desc: 'Demi fromage Rose et demi Fleur d\'ail de la Vache à Maillotte, saucissons, cornichons, olives et oitas',
            price: '22,99 $',
          },
          {
            name: 'Les pépites Vam',
            desc: 'Fromages en grains de la Vache à Maillotte panés avec sauce au choix',
            price: '14,99 $',
          },
          {
            name: 'Fromage brie chaud au sirop d\'érable',
            desc: 'Sirop d\'érable de l\'Abitibi-Témiscamingue, pain baguette',
            price: '14,99 $',
          },
          { name: 'Extra pacanes', price: '1,00 $' },
          { name: 'Extra canneberges', price: '1,00 $' },
        ],
      },
      {
        label: 'Pizzas — gracieuseté Double Pizza La Sarre',
        items: [
          { name: 'Pepperoni', price: '14,99 $' },
          { name: 'All Dress', price: '14,99 $' },
          { name: 'Méditerranéenne (vég.)', price: '14,99 $' },
          { name: 'Pepperoni bacon', price: '15,99 $' },
          { name: 'Poulet thaï', price: '15,99 $' },
        ],
      },
      {
        label: 'Tartares de bœuf — Boucherie du Brack (1 pers.)',
        items: [
          { name: 'Tartare Original', price: '24,99 $' },
          { name: 'Tartare Cornichon / Feta', price: '26,99 $' },
          { name: 'Tartare Câpre / Feta', price: '26,99 $' },
          { name: 'Tartare Jerky de bacon / Pacane grillée', price: '28,99 $' },
          { name: 'Tartare Bacon / Feta', price: '28,99 $' },
        ],
      },
      {
        label: '🆕 Bretzel géant',
        items: [
          {
            name: 'Bretzel géant (collaboration L\'accalmie chez Juliette)',
            desc: 'Choix de sauces : moutarde et miel · ➕ choix de caramel',
            price: '9,99 $',
          },
        ],
      },
      {
        label: 'Salade & Quésadillas',
        items: [
          {
            name: 'Salade Grecque crémeuse — repas',
            desc: 'Tomates cerise, concombre, feta et sauce tzatziki',
            price: '10,99 $',
          },
          {
            name: 'Quésadillas de l\'Alambic',
            desc: 'Jerky de poulet du Brack, piment, tomate, fromage râpé, sauce du Guernier. Avec chips maison Écorces du Diable',
            price: '18,99 $',
          },
        ],
      },
      {
        label: 'Nachos',
        items: [
          { name: 'Nachos Original', price: '17,99 $' },
          { name: 'Nachos Bacon', price: '22,99 $' },
          { name: 'Nachos Jerky de poulet', price: '24,99 $' },
          { name: 'Extra (bacon, poulet, jerky whisky)', price: '4,00 $' },
          { name: 'Extra fromage', price: '3,00 $' },
        ],
      },
      {
        label: 'Grilled Cheese — pain blanc Boulangerie Lacroix',
        items: [
          {
            name: 'Le Conservateur',
            desc: 'Fromage cheddar',
            price: '11,99 $',
          },
          {
            name: 'Le Sucré Salé',
            desc: 'Fromage brie, tranche de pomme',
            price: '14,99 $',
          },
          {
            name: '⭐ Le Sucré Salé Spécial',
            desc: 'Fromage brie, tranche de pomme, jerky de bacon — ⭐ coup de cœur des clients',
            price: '17,99 $',
          },
          {
            name: 'Le Spécial Jerky de l\'Alambic',
            desc: 'Fromage cheddar, jerky au whisky',
            price: '16,99 $',
          },
          {
            name: 'Le Smoked Meat',
            desc: 'Fromage cheddar, smoked meat du Brack',
            price: '16,99 $',
          },
          {
            name: 'Spécial Jerky de Bacon',
            desc: 'Fromage cheddar, grillade de bacon du Brack',
            price: '16,99 $',
          },
          {
            name: 'Le Décadent',
            desc: 'Fromage cheddar, salami, jerky au whisky, bacon',
            price: '22,99 $',
          },
          { name: 'Choix d\'extra (fromage brie ou cheddar)', price: '3,50 $' },
        ],
      },
      {
        label: 'Jerky & Chips maison',
        items: [
          {
            name: 'Jerky Spécial l\'Alambic (portion individuelle)',
            desc: 'Jerky de la Boucherie du Brack, whisky et assaisonnement',
            price: '8,50 $',
          },
          {
            name: 'Jerky au poulet du Brack (portion individuelle)',
            price: '9,00 $',
          },
          {
            name: '🆕 Bol de chips maison Écorces du Diable',
            desc: 'Choix natures, assaisonnées au sel et vinaigre',
            price: '2,99 $',
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════
  // (Section « Plateaux de dégustation » déplacée sur /degustation
  //  pour mettre en valeur la signature au bon endroit.)
  // ═══════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════
  // 02 — DRINKS (cocktails maison)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'drinks',
    num: '02',
    title: 'Drinks',
    subtitle: 'Cocktails travaillés et classiques revisités',
    groups: [
      {
        label: 'Cocktails',
        items: [
          { name: 'Amaretto Sour', desc: 'Amaretto, bar mix', price: '11,75 $' },
          {
            name: 'Amaretto Whisky Sour',
            desc: 'Amaretto, Jack Daniel\'s No 7, sirop d\'érable, jus de citron, bar mix',
            price: '10,75 $',
          },
          {
            name: 'Aperol Spritz',
            desc: 'Jus de lime, sirop simple, jus d\'ananas, Aperol',
            price: '8,50 $',
          },
          {
            name: 'Daiquiri',
            desc: 'Bols aux bananes, rhum blanc, purée de fraise, slush',
            price: '10,75 $',
          },
          {
            name: 'Fruit de la Passion',
            desc: 'Gin Roméo Zay de Fouki, sirop fruit de la passion, jus de canneberge blanche, slush',
            price: '10,50 $',
          },
          {
            name: 'Gin & Tonic',
            desc: 'Choisis ton gin parmi nos multiples choix, tonic ou 7up',
            price: '11,25 $',
          },
          {
            name: 'Kérosène',
            desc: 'Bombay Bramble, vodka framboise, Chambord, Kombustible (Ékorce Kombucha)',
            price: '16,75 $',
          },
        ],
      },
      {
        label: 'Martinis',
        items: [
          {
            name: 'Dry Martini',
            desc: 'Bombay Sapphire, martini sec',
            price: '10,75 $',
          },
          {
            name: 'Martini Choco-Espresso',
            desc: 'Crème glacée lait au chocolat, vodka, espresso',
            price: '13,25 $',
          },
          {
            name: 'Dirty Martini',
            desc: 'Bombay Sapphire, martini sec, jus d\'olive',
            price: '13,75 $',
          },
          {
            name: 'Martini Classique',
            desc: 'Bombay Sapphire, martini sec',
            price: '15,00 $',
          },
        ],
      },
      {
        label: 'Mojitos',
        items: [
          {
            name: 'Mojitos classiques',
            desc: 'Rhum blanc, soda, menthe, sucre — verre / petit pichet / gros pichet',
            price: '10,75 $ / 20,50 $ / 37,00 $',
          },
          {
            name: 'Mojitos aux framboises',
            desc: 'Bombay Bramble, Sour Puss, soda, menthe',
            price: '11,95 $',
          },
          {
            name: 'Mojitos fruit de la passion',
            desc: 'Rhum blanc, fruit de la passion, soda, purée, menthe',
            price: '11,75 $',
          },
          {
            name: 'Mojitos fruit du dragon',
            desc: 'Rhum blanc, 7up, soda, sirop fruit du dragon, menthe',
            price: '11,75 $',
          },
        ],
      },
      {
        label: 'Verre ou pichet — verre / petit pichet / gros pichet',
        items: [
          { name: 'Adios Motherfucker',  price: '12,75 $ / 23,00 $ / 41,00 $' },
          { name: 'Alambic Caesar',      price: '11,95 $ / 21,00 $ / 38,00 $' },
          { name: 'Alambic des Dieux',   price: '11,95 $ / 21,50 $ / 38,50 $' },
          { name: 'Alambic Spicy',       price: '12,95 $ / 23,50 $ / 41,50 $' },
          { name: 'Alambic Tropical',    price: '11,75 $ / 21,00 $ / 38,00 $' },
          { name: 'Blue Lagun',          price: '11,75 $ / 21,00 $ / 38,00 $' },
          { name: 'Long Island Ice Tea', price: '14,75 $ / 26,50 $ / 47,75 $' },
          { name: 'Pineapple Fizz',      price: '10,75 $ / 20,00 $ / 36,00 $' },
          { name: 'Raspberry Bomb',      price: '10,75 $ / 20,00 $ / 36,00 $' },
          { name: 'Rhum & Coke',         price: '10,75 $ / 20,00 $ / 36,00 $' },
          { name: 'Strawberry Bomb',     price: '10,75 $ / 20,00 $ / 36,00 $' },
          { name: 'Victoria\'s Secret',  price: '10,75 $ / 20,00 $ / 36,00 $' },
        ],
      },
      {
        label: 'Sangrias — verre / petit pichet / gros pichet',
        items: [
          {
            name: 'Sangria aux framboises',
            desc: 'Vin rosé, Chambord, Southern Comfort, vodka aux framboises, jus de canneberge, 7up',
            price: '12,75 $ / 24,00 $ / 43,00 $',
          },
          {
            name: 'Sangria blanche',
            desc: 'Vin blanc, schnapps aux pêches, téquila, 7up',
            price: '12,75 $ / 24,00 $ / 43,00 $',
          },
          {
            name: 'Sangria rouge',
            desc: 'Vin rouge, Cointreau, Havana Club, jus d\'orange, 7up',
            price: '12,75 $ / 24,00 $ / 43,00 $',
          },
          {
            name: 'Sangria bleue',
            desc: 'Vin blanc, Curaçao bleu, jus de canneberges, 7up',
            price: '12,75 $ / 24,00 $ / 43,00 $',
          },
        ],
      },
      {
        label: 'Cafés alcoolisés',
        items: [
          { name: 'Café Bailey\'s salé', price: '9,75 $' },
          {
            name: 'Café des bois',
            desc: 'Coureur des bois érable et bleuet, sirop d\'érable, café',
            price: '10,75 $',
          },
          {
            name: 'Café Irlandais',
            desc: 'Bailey\'s, whisky irlandais, café, crème fouettée',
            price: '13,75 $',
          },
        ],
      },
      {
        label: '❄️ Cocktails saisonniers',
        items: [
          {
            name: 'Doux Péché',
            desc: 'Chocolat chaud, rhum cacao vanille',
            price: '9,75 $',
          },
          {
            name: 'Fée des glaces',
            desc: 'Gin Jardin Verde, triple sec, jus de canneberge blanc, 7up',
            price: '11,95 $',
          },
          {
            name: 'Santa Claus',
            desc: 'Smirnoff red white merry, Kombustible aux framboises',
            price: '12,50 $',
          },
          {
            name: 'Mystère des fêtes',
            desc: 'Téquila, Curaçao bleu, jus de canneberge orange, 7up',
            price: '10,95 $',
          },
          {
            name: 'Baies givrées',
            desc: 'Vodka, Chambord, purée de framboise, limonade, slush',
            price: '12,50 $',
          },
          {
            name: 'Le Grinch',
            desc: 'Midori, jus d\'orange, grenadine, 7up',
            price: '12,00 $',
          },
          {
            name: 'Berry Mommy',
            desc: 'Smirnoff framboise, limoncello, bols banane, bar mix, framboises',
            price: '10,95 $',
          },
          {
            name: 'Le Pétillant',
            desc: 'Vin blanc, jus de canneberge, eau pétillante',
            price: '8,95 $',
          },
          {
            name: 'Coureur des bois',
            desc: 'Coureur des bois, jus de canneberge, jus d\'orange',
            price: '8,95 $',
          },
          {
            name: 'Pommegrenade épicé',
            desc: 'Rhum épicé, sirop pommegrenade Monin, jus de canneberge, jus de citron',
            price: '9,95 $',
          },
          {
            name: 'Cocktail Ungava Fraise Yuzu',
            desc: 'Gin Fraise Yuzu, jus de pomme, jus de lime, jus de canneberge',
            price: '8,95 $',
          },
        ],
      },
      {
        label: '🥃 Shooters des fêtes',
        items: [
          {
            name: 'Le Polaire',
            desc: 'Bailey\'s, crème de menthe',
            price: '5,00 $',
          },
          {
            name: 'Plaisir Coupable',
            desc: 'Crème pistache, boisson Brownies & S\'mores',
            price: '5,00 $',
          },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════
  // 04 — AU OZ (spiritueux à l'once)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'au-oz',
    num: '03',
    title: "Au oz",
    subtitle: "Spiritueux servis à l'once",
    groups: [
      {
        label: 'Rhum',
        items: [
          { name: 'Bumbu', price: '11,00 $ / oz' },
          { name: 'Captain Morgan Original Épicé', price: '5,75 $ / oz' },
          { name: 'Chic Choc', price: '6,75 $ / oz' },
          { name: 'Chic Choc noir', price: '6,75 $ / oz' },
          { name: 'Havana Club Añejo Reserva (brun)', price: '5,75 $ / oz' },
          { name: 'Havana Club Original (blanc)', price: '5,25 $ / oz' },
          { name: 'Moonlight MattLang', price: '8,75 $ / oz' },
          { name: 'Crème de rhum', price: '7,50 $ / oz' },
          { name: 'Morbleu Chocolat', price: '6,75 $ / oz' },
          { name: 'Rhum épicé Sainte-Marie', price: '5,75 $ / oz' },
          { name: "Rhum Melon d'eau", price: '7,00 $ / oz' },
          { name: 'Rhum Noix de Coco Malibu', price: '5,00 $ / oz' },
          { name: 'Plantation', price: '6,75 $ / oz' },
        ],
      },
      {
        label: 'Téquila',
        items: [
          { name: 'Patrón Téquila', price: '14,00 $ / oz' },
          { name: 'Cherry River Téquila', price: '6,75 $ / oz' },
        ],
      },
      {
        label: 'Cognac',
        items: [
          { name: 'Courvoisier', price: '15,00 $ / oz' },
          { name: 'Gauthier', price: '8,75 $ / oz' },
          { name: 'Larsen', price: '11,00 $ / oz' },
        ],
      },
      {
        label: 'Vodka',
        items: [
          { name: 'Art. in Res. Framboise bleu', price: '5,00 $ / oz' },
          { name: 'New Amsterdam Pink Whitney', price: '5,25 $ / oz' },
          { name: 'Vodka 3 Lacs Pêche', price: '5,00 $ / oz' },
          { name: 'Vodka Bold', price: '4,00 $ / oz' },
        ],
      },
      {
        label: 'Autres spiritueux',
        items: [
          { name: 'Absinthe Balsac', price: '8,50 $ / oz' },
          { name: 'Air Limonade', price: '3,50 $ / oz' },
          { name: 'Alcool 40 %', price: '4,00 $ / oz' },
          { name: 'Alizé Fruit de la passion', price: '4,50 $ / oz' },
          { name: 'Amaretto Équinoxe', price: '6,25 $ / oz' },
          { name: "Amaretto O'Dwyer Dartmouth", price: '6,00 $ / oz' },
          { name: "Bailey's original", price: '5,50 $ / oz' },
          { name: "Butterscotch Dr. McGillicuddy's Caramel", price: '3,50 $ / oz' },
          { name: 'Chambord', price: '7,75 $ / oz' },
          { name: "Coureur des bois Bleuets ou Crème d'érable", price: '6,00 $ / oz' },
          { name: 'Cremaglace Chocolat ou vanille', price: '5,00 $ / oz' },
          { name: 'Crème de menthe blanche', price: '3,50 $ / oz' },
          { name: 'Crème de menthe Chocolat Artis & Residence', price: '5,00 $ / oz' },
          { name: 'Curaçao Marie Brizard', price: '4,00 $ / oz' },
          { name: 'De Kuyper Peachtree Schnapps', price: '4,00 $ / oz' },
          { name: "Fleur de l'Île Triple sec", price: '6,25 $ / oz' },
          { name: 'Frangelico', price: '5,00 $ / oz' },
          { name: 'Galliano liqueur de vanille', price: '5,75 $ / oz' },
          { name: 'Goldshlager', price: '5,50 $ / oz' },
          { name: 'Grand Marnier liqueur', price: '9,00 $ / oz' },
          { name: 'Humble Banana', price: '5,75 $ / oz' },
          { name: 'Humble Framboise', price: '5,75 $ / oz' },
          { name: 'Humble Litchi', price: '5,75 $ / oz' },
          { name: 'Jagermeister', price: '5,50 $ / oz' },
          { name: 'Limoncello Luxardo', price: '4,50 $ / oz' },
          { name: 'Limoncello Poli Elisir Limone', price: '5,00 $ / oz' },
          { name: 'Martini Sec', price: '2,00 $ / oz' },
          { name: "Meaghers Abricot Brandy", price: '4,75 $ / oz' },
          { name: 'Mentherie', price: '5,00 $ / oz' },
          { name: 'Midori Boisson au melon', price: '5,50 $ / oz' },
          { name: 'Ramazzotti Sambuca', price: '4,25 $ / oz' },
          { name: 'Sambuca Noire', price: '6,25 $ / oz' },
          { name: 'Sivo Framboises', price: '5,00 $ / oz' },
          { name: 'Southern Comfort', price: '4,75 $ / oz' },
          { name: 'Spritz des îles', price: '4,75 $ / oz' },
          { name: 'Tia Maria', price: '5,50 $ / oz' },
          { name: 'Triple sec Cherry River', price: '6,75 $ / oz' },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════
  // 05 — SANS ALCOOL
  // ═══════════════════════════════════════════════════════════
  {
    id: 'sans-alcool',
    num: '04',
    title: 'Sans alcool',
    subtitle: 'Breuvages, kombuchas locaux et bières 0 %',
    groups: [
      {
        label: 'Breuvages sans alcool',
        items: [
          { name: 'Redbull', price: '8,00 $' },
          { name: 'Bloody Virgin', price: '8,00 $' },
          { name: 'Raspberry Bomb (sans alcool)', price: '8,00 $' },
          { name: 'Strawberry Bomb (sans alcool)', price: '8,00 $' },
          { name: 'Shirley Temple', price: '8,00 $' },
          {
            name: 'Tango (Sangria · Mojitos · Gin Tonic)',
            desc: 'Nouveauté locale',
            price: '6,75 $',
          },
          { name: 'Guru sans sucre', price: '7,50 $' },
        ],
      },
      {
        label: 'Kombustible Kombucha',
        items: [
          { name: 'Kombustible Ananas', price: '8,00 $' },
          { name: 'Kombustible Framboise', price: '8,00 $' },
          { name: 'Kombustible Pêche', price: '8,00 $' },
          { name: 'Kombustible Cerise noire', price: '8,00 $' },
        ],
      },
      {
        label: 'Ékorce Kombucha',
        items: [
          { name: 'Ékorce Fraise et Basilic', price: '8,00 $' },
          { name: 'Ékorce Bleuet sauvage', price: '8,00 $' },
        ],
      },
    ],
  },
];

export default function PublicMenu() {
  useDocumentMeta({
    title: "Menu — Bar L'Alambic",
    description:
      "Notre carte : whiskys, microbrasseries québécoises, gins, rhums, cocktails travaillés, vins d'importation privée, tartares, plateaux de dégustation et grilled cheese. Bar L'Alambic à La Sarre.",
  });

  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  // Active section detection via IntersectionObserver
  useEffect(() => {
    const sections = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (n): n is HTMLElement => n !== null
    );

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.2, 0.5, 1] }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <PublicPageHero
        photo={MENU_HERO}
        objectPosition={HERO_OBJECT_POSITION[MENU_HERO]}
        eyebrow={
          <>
            <BookOpen className="h-3 w-3" />
            La carte
          </>
        }
        title={
          <>
            Notre <span className="italic text-[#D4A574]">menu</span>
          </>
        }
        description="Une sélection en perpétuelle évolution. Une nouvelle bouteille rejoint la carte chaque mois. Demandez-nous ce qu'on a reçu cette semaine."
      />

      {/* ───────── Subnav sticky ───────── */}
      <nav className="sticky top-[57px] md:top-[65px] z-40 border-b border-[#1F1F1F] bg-[#0A0A0A]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-2 md:px-8">
          <ul className="flex gap-1 overflow-x-auto py-2 scrollbar-thin">
            {SECTIONS.map((s) => (
              <li key={s.id} className="shrink-0">
                <a
                  href={`#${s.id}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium tracking-wide whitespace-nowrap transition-colors',
                    activeId === s.id
                      ? 'bg-[#D4A574]/15 text-[#D4A574]'
                      : 'text-neutral-400 hover:text-[#E8E0D6] hover:bg-[#141414]'
                  )}
                >
                  <span className="font-display tabular-nums opacity-70">
                    {s.num}
                  </span>
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* ───────── Sections (+ image-breaks insérés après certaines) ───────── */}
      {SECTIONS.map((section, i) => (
        <Fragment key={section.id}>
        <motion.section
          id={section.id}
          className={cn(
            'border-b border-[#1F1F1F] scroll-mt-32',
            i % 2 === 1 && 'bg-[#0C0C0C]'
          )}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={staggerContainer(0.04, 0.05)}
        >
          <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20 md:py-24">
            {/* Header section */}
            <motion.div
              variants={fadeUp}
              className="flex items-baseline gap-4 mb-12"
            >
              <motion.span
                variants={scaleIn}
                className="font-display text-5xl md:text-6xl text-[#D4A574]/40 tabular-nums leading-none"
              >
                {section.num}
              </motion.span>
              <div>
                <h2 className="font-display text-3xl md:text-5xl text-[#E8E0D6] leading-tight">
                  {section.title}
                </h2>
                <p className="mt-2 text-sm text-neutral-500">{section.subtitle}</p>
              </div>
            </motion.div>

            {/* Groups */}
            <div className="space-y-10">
              {section.groups.map((group, gi) => (
                <motion.div key={gi} variants={fadeUp}>
                  {group.label && (
                    <h3 className="font-display italic text-xl text-[#D4A574] mb-5 flex items-center gap-3">
                      <span>{group.label}</span>
                      <span className="flex-1 h-px bg-[#1F1F1F]" />
                    </h3>
                  )}
                  <ul
                    className={cn(
                      'space-y-4',
                      group.items.length > 8 && 'md:columns-2 md:gap-x-12'
                    )}
                  >
                    {group.items.map((item, ii) => (
                      <li key={ii} className="break-inside-avoid group/item">
                        <div className="flex items-baseline gap-3">
                          <span className="text-[#E8E0D6] transition-colors group-hover/item:text-[#D4A574]">
                            {item.name}
                          </span>
                          <span className="flex-1 mx-2 border-b border-dotted border-[#2A2A2A] mb-1.5" />
                          {item.price && (
                            <span className="text-[#D4A574] tabular-nums whitespace-nowrap text-sm">
                              {item.price}
                            </span>
                          )}
                        </div>
                        {item.desc && (
                          <p className="text-xs text-neutral-500 italic mt-1 pl-1 leading-snug">
                            {item.desc}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
        {MENU_BREAKS[i] && (
          <MenuImageBreak
            photo={MENU_BREAKS[i].photo}
            quote={MENU_BREAKS[i].quote}
            attribution={MENU_BREAKS[i].attribution}
          />
        )}
        </Fragment>
      ))}

      {/* ───────── CTA retour accueil ───────── */}
      <motion.section
        className="py-20 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer(0.1)}
      >
        <motion.p
          variants={fadeUp}
          className="text-xs uppercase tracking-[0.25em] text-neutral-600 mb-4"
        >
          La carte évolue chaque mois
        </motion.p>
        <motion.h3
          variants={fadeUp}
          className="font-display italic text-2xl md:text-3xl text-[#D4A574] mb-8"
        >
          Venez voir ce qu'on a reçu
        </motion.h3>
        <motion.div
          variants={fadeUp}
          className="flex items-center justify-center gap-3 flex-wrap"
        >
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#2A2A2A] text-[#E8E0D6] hover:bg-[#1A1A1A] transition-all text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour à l'accueil
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
            <Link
              to="/reserver"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#D4A574] text-[#0A0A0A] font-medium hover:bg-[#C9A36B] transition-all text-sm shadow-lg shadow-[#D4A574]/20"
            >
              Réserver une table
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </motion.div>
      </motion.section>
    </>
  );
}
